import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Calculator, Trash2, Phone, Mail, Calendar, TrendingDown, Download } from 'lucide-react';
import { calculatorAPI } from '../../lib/api';
import { formatDateTime } from '../../lib/utils';
import { fmtBRL, REGIME_LABELS, SERVICE_LABELS, PROFESSION_LABELS } from '../../components/calculator/calcUtils';
import { toast } from 'sonner';

const StatCard = ({ icon: Icon, color, value, label }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-6">
    <div className="flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-sm text-slate-500">{label}</p>
      </div>
    </div>
  </div>
);

const serviceDisplay = (lead) => {
  const service = SERVICE_LABELS[lead.main_service] || lead.main_service || '—';
  const prof = PROFESSION_LABELS[lead.non_medical_profession] || '';
  return prof ? `${service} (${prof})` : service;
};

const AdminCalculatorLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => { loadLeads(); }, []);

  const loadLeads = async () => {
    try {
      const response = await calculatorAPI.getLeads();
      setLeads(response.data);
    } catch (error) {
      toast.error('Erro ao carregar leads da calculadora');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await calculatorAPI.deleteLead(deleteId);
      toast.success('Lead excluído com sucesso');
      loadLeads();
    } catch (error) {
      toast.error('Erro ao excluir lead');
    } finally {
      setDeleteId(null);
    }
  };

  const handleExport = () => {
    const csv = [
      ['Nome', 'Telefone', 'Email', 'Faturamento Mensal', 'Folha Mensal', 'Servico', 'Regime Atual', 'Municipio', 'Melhor Regime', 'Economia Mensal', 'Data'].join(','),
      ...leads.map(l => [
        l.name, l.phone, l.email || '', l.monthly_revenue, l.monthly_payroll,
        `"${serviceDisplay(l)}"`,
        REGIME_LABELS[l.current_regime] || l.current_regime, l.municipality,
        `"${l.best_regime}"`, l.savings_monthly ?? '', formatDateTime(l.created_at),
      ].join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-calculadora-apex-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Arquivo exportado');
  };

  const thisMonth = leads.filter(l => {
    const d = new Date(l.created_at);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  const totalSavings = leads.reduce((acc, l) => acc + (l.savings_monthly || 0), 0);

  return (
    <div data-testid="admin-calculator-leads">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Leads da Calculadora</h1>
          <p className="text-slate-600">Médicos que simularam seus impostos no site</p>
        </div>
        <Button variant="outline" onClick={handleExport} disabled={leads.length === 0} className="btn-secondary" data-testid="calc-leads-export-button">
          <Download className="w-4 h-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatCard icon={Calculator} color="bg-blue-100 text-blue-600" value={leads.length} label="Total de simulações" />
        <StatCard icon={Calendar} color="bg-green-100 text-green-600" value={thisMonth} label="Este mês" />
        <StatCard icon={TrendingDown} color="bg-emerald-100 text-emerald-600" value={fmtBRL(totalSavings)} label="Economia mensal identificada (soma)" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome / Contato</TableHead>
                <TableHead>Faturamento</TableHead>
                <TableHead>Folha</TableHead>
                <TableHead>Serviço</TableHead>
                <TableHead>Regime atual</TableHead>
                <TableHead>Município</TableHead>
                <TableHead>Melhor regime</TableHead>
                <TableHead>Economia/mês</TableHead>
                <TableHead>Data</TableHead>
                <TableHead className="w-[60px]">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array(5).fill(0).map((_, i) => (
                  <TableRow key={i}>
                    {Array(9).fill(0).map((_, j) => (
                      <TableCell key={j}><div className="skeleton h-4 w-20" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : leads.length > 0 ? (
                leads.map((lead) => (
                  <TableRow key={lead.id} data-testid={`calc-lead-row-${lead.id}`}>
                    <TableCell>
                      <p className="font-medium text-slate-900">{lead.name}</p>
                      <a
                        href={`https://wa.me/55${lead.phone.replace(/\D/g, '')}`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-slate-600 hover:text-green-600"
                      >
                        <Phone className="w-3.5 h-3.5" />
                        {lead.phone}
                      </a>
                      {lead.email && (
                        <a href={`mailto:${lead.email}`} className="flex items-center gap-1 text-sm text-slate-600 hover:text-primary">
                          <Mail className="w-3.5 h-3.5" />
                          {lead.email}
                        </a>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-700">{fmtBRL(lead.monthly_revenue)}</TableCell>
                    <TableCell className="text-slate-700">{fmtBRL(lead.monthly_payroll)}</TableCell>
                    <TableCell className="text-slate-600 text-sm">{serviceDisplay(lead)}</TableCell>
                    <TableCell>
                      <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs">
                        {REGIME_LABELS[lead.current_regime] || lead.current_regime}
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm">{lead.municipality}</TableCell>
                    <TableCell className="text-slate-700 text-sm max-w-[180px]">{lead.best_regime || '—'}</TableCell>
                    <TableCell>
                      {lead.savings_monthly > 0 ? (
                        <span className="font-semibold text-emerald-600">{fmtBRL(lead.savings_monthly)}</span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">{formatDateTime(lead.created_at)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteId(lead.id)} className="text-red-500 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12">
                    <Calculator className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Nenhuma simulação realizada ainda.</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir lead?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O lead será permanentemente excluído.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminCalculatorLeads;
